import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, MessageCircle, QrCode, RefreshCw, Smartphone, Trash2 } from 'lucide-react';
import { Alert, Badge, Button, Card, CardHeader, ConfirmDialog, Field, Input, Select } from '../ui';
import { adminService } from '../../services/agencyService';

// Canal de salida propio de MARKETOS. No reaprovecha la pantalla de un
// cliente: el administrador conecta el número empresarial una sola vez y
// después IA y MCP lo usa exclusivamente para los avisos operativos.
export default function WhatsAppMarketos({ clients, onConnected }) {
  const [channels, setChannels] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [channelName, setChannelName] = useState('WhatsApp empresarial MARKETOS');
  const [qrChannelId, setQrChannelId] = useState(null);
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState(null);
  const [qrError, setQrError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const result = await adminService.listWhatsAppChannels();
      setChannels(result.channels || []);
    } catch (error) {
      setNotice({ tone: 'danger', text: error.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!selectedClientId && clients.length) setSelectedClientId(String(clients[0].id));
  }, [clients, selectedClientId]);

  useEffect(() => {
    if (!qrChannelId) return undefined;
    const poll = async () => {
      try {
        const result = await adminService.whatsAppQr(qrChannelId);
        setStatus(result.status || 'connecting');
        setQr(result.qr || null);
        setQrError(result.error || null);
        if (result.status === 'open') {
          clearInterval(pollRef.current);
          await load();
          onConnected?.();
          setNotice({ tone: 'success', text: 'WhatsApp empresarial conectado. Ya puedes elegirlo en IA y MCP → Avisos por WhatsApp.' });
        }
        if (['closed', 'error', 'disconnected'].includes(result.status)) clearInterval(pollRef.current);
      } catch (error) {
        setNotice({ tone: 'danger', text: error.message });
      }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => clearInterval(pollRef.current);
  }, [qrChannelId, load, onConnected]);

  const start = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await adminService.startWhatsAppChannel({
        client_id: Number(selectedClientId), name: channelName.trim()
      });
      setQrChannelId(result.channel.id);
      setStatus('connecting');
      setQr(null);
      setQrError(null);
    } catch (error) {
      setNotice({ tone: 'danger', text: error.message });
    } finally { setBusy(false); }
  };

  const reconnect = async (channel) => {
    setBusy(true);
    setNotice(null);
    try {
      await adminService.reconnectWhatsAppChannel(channel.id);
      setQrChannelId(channel.id);
      setStatus('connecting');
      setQr(null);
      setQrError(null);
    } catch (error) {
      setNotice({ tone: 'danger', text: error.message });
    } finally { setBusy(false); }
  };

  const deleteChannel = async () => {
    if (!channelToDelete) return;
    setDeleting(true);
    try {
      await adminService.deleteWhatsAppChannel(channelToDelete.id);
      if (Number(qrChannelId) === Number(channelToDelete.id)) {
        setQrChannelId(null); setQr(null); setStatus(null); setQrError(null);
      }
      setChannelToDelete(null);
      await load();
      setNotice({ tone: 'success', text: 'Canal eliminado. Puedes crear uno nuevo cuando quieras.' });
    } catch (error) {
      setNotice({ tone: 'danger', text: error.message });
    } finally { setDeleting(false); }
  };

  const technicalClient = clients.find((client) => String(client.id) === String(selectedClientId));

  return (
    <div className="space-y-5">
      <Alert tone="info" title="WhatsApp empresarial de MARKETOS">
        Conecta aquí el número que enviará avisos a tu teléfono y a los clientes. El QR solo se muestra en tu panel de administrador.
      </Alert>

      {notice && <Alert tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Alert>}

      <Card className="overflow-hidden border-sky-100 bg-gradient-to-br from-white via-sky-50 to-navy-50">
        <CardHeader
          icon={<Smartphone className="h-5 w-5" />}
          title="Conectar número empresarial"
          subtitle="Se vincula como WhatsApp Web: no necesitas una API externa ni compartir claves."
        />
        <div className="space-y-4 bg-gradient-to-r from-white/80 via-sky-50/50 to-white/80 p-4 sm:p-5">
          {qrChannelId ? (
            <div className="mx-auto max-w-sm rounded-2xl border border-sky-100 bg-white/80 p-5 text-center shadow-sm">
              {status === 'open' ? (
                <>
                  <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
                  <p className="mt-3 font-bold text-navy-900">WhatsApp conectado</p>
                  <p className="mt-1 text-sm text-navy-900/60">Ahora ve a IA y MCP para configurar quién recibe los avisos.</p>
                </>
              ) : qr ? (
                <>
                  <p className="mb-3 text-sm text-navy-900/70">En el teléfono empresarial abre WhatsApp Business → Dispositivos vinculados → Vincular un dispositivo y escanea este código.</p>
                  <img src={qr} alt="Código QR para conectar el WhatsApp empresarial" className="mx-auto h-56 w-56 rounded-xl border border-navy-100 bg-white p-2" />
                  <p className="mt-3 text-xs text-navy-900/50">Mantén esta página y el backend abiertos mientras escaneas.</p>
                </>
              ) : ['closed', 'error', 'disconnected'].includes(status) ? (
                <>
                  <p className="text-sm font-bold text-magenta-700">No se pudo generar el QR.</p>
                  <p className="mt-2 text-sm text-navy-900/65">{qrError || 'La conexión con WhatsApp se cerró antes de completar el escaneo.'}</p>
                  <Button className="mt-4" variant="soft" onClick={() => { setQrChannelId(null); setQr(null); setStatus(null); setQrError(null); }}>Volver a los canales</Button>
                </>
              ) : (
                <p className="py-8 text-sm text-navy-900/60">Generando código QR…</p>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Cuenta técnica" required hint="Aquí se guardará el historial del número MARKETOS; no cambia los permisos de ese cliente.">
                  <Select value={selectedClientId} onChange={(event) => setSelectedClientId(event.target.value)}>
                    <option value="">Selecciona una cuenta</option>
                    {clients.map((client) => <option key={client.id} value={client.id}>{client.display_name || client.name}</option>)}
                  </Select>
                </Field>
                <Field label="Nombre del WhatsApp" required>
                  <Input value={channelName} onChange={(event) => setChannelName(event.target.value)} placeholder="WhatsApp empresarial MARKETOS" />
                </Field>
              </div>
              {technicalClient && <p className="text-xs text-navy-900/55">Para esta prueba local puedes usar: <strong>{technicalClient.display_name || technicalClient.name}</strong>.</p>}
              <div className="flex justify-end border-t border-navy-50 pt-4">
                <Button
                  variant="secondary"
                  className="!bg-[#071D53] !text-white hover:!bg-[#0B2E72]"
                  icon={<QrCode className="h-4 w-4" />}
                  loading={busy}
                  disabled={!selectedClientId || !channelName.trim()}
                  onClick={start}
                >
                  Generar código QR
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden border-magenta-100 bg-gradient-to-br from-white via-magenta-50/40 to-sky-50">
        <CardHeader
          icon={<MessageCircle className="h-5 w-5" />}
          title="Canales empresariales"
          subtitle="Estado en vivo de los WhatsApp conectados desde Operación MARKETOS."
          actions={<Button size="sm" variant="soft" icon={<RefreshCw className="h-4 w-4" />} onClick={load}>Actualizar</Button>}
        />
        <div className="space-y-3 bg-gradient-to-b from-white/70 to-sky-50/50 p-4 sm:p-5">
          {channels.length === 0 ? <p className="text-sm text-navy-900/60">Todavía no hay un WhatsApp empresarial conectado.</p> : channels.map((channel) => (
            <div key={channel.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-navy-100 bg-gradient-to-r from-navy-50 via-white to-sky-50 p-3 shadow-sm">
              <div>
                <p className="font-bold text-navy-900">{channel.name}</p>
                <p className="text-xs text-navy-900/55">Cuenta técnica: {channel.client_name}</p>
                {channel.error && <p className="mt-1 max-w-xl text-xs text-magenta-700">Motivo: {channel.error}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={channel.connected ? 'green' : channel.status === 'error' ? 'magenta' : 'gold'}>{channel.connected ? 'Conectado' : channel.status === 'connecting' ? 'Conectando' : channel.status === 'error' ? 'Necesita revisión' : 'Desconectado'}</Badge>
                {!channel.connected && <Button size="sm" variant="soft" loading={busy} onClick={() => reconnect(channel)}>Reconectar</Button>}
                <Button size="sm" variant="danger" icon={<Trash2 className="h-4 w-4" />} onClick={() => setChannelToDelete(channel)}>Borrar</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <ConfirmDialog
        open={!!channelToDelete}
        title="¿Borrar este canal?"
        message={`Se eliminará “${channelToDelete?.name || ''}” y su intento de conexión. No borra mensajes de tu teléfono. Tendrás que escanear un QR nuevo si lo vuelves a crear.`}
        confirmLabel="Sí, borrar canal"
        tone="danger"
        loading={deleting}
        onCancel={() => setChannelToDelete(null)}
        onConfirm={deleteChannel}
      />
    </div>
  );
}
